<template>
	<div class="bot-bar">
		<!-- 全选复选框 -->
		<div class="select-all">
			<van-checkbox v-model="isChecked" icon-size="1.5rem" @click="handleClick">全部</van-checkbox>
		</div>

		<!-- 结算区域 -->
		<div class="submit-section">
			<div class="total-amount">
				<span class="label">合计：</span>
				<span v-if="showMaskedTotal" class="masked-total">****</span>
				<ShowAmount v-else :price="totalSum" />
			</div>

			<van-button type="danger" class="submit-btn" @click="handleSubmit">提交订单</van-button>
		</div>
	</div>
</template>

<script setup>
import { ref, watch } from 'vue';
import ShowAmount from '@/components/ShowAmount.vue';
import { math } from '@/utils/business/utilsMath.js';
import { getShoppingCartActivity } from '@/api/buyCar.js';
import { showToast } from 'vant';
import { setStorageSync } from '@/utils/storage.js';
import { isCooperationPrice } from '@/utils/productPrice';
import { useAnalytics } from '@/composables/useAnalytics';
const props = defineProps({
	lists: {
		type: Array,
		default: () => [],
	},
});

const emit = defineEmits(['selectAll']);

const { trackEvent } = useAnalytics();

const isChecked = ref(false);
const totalSum = ref(0);
const showMaskedTotal = ref(false);

// 监听列表变化，计算总金额
watch(
	() => props.lists,
	newVal => {
		calculateTotal(newVal);
	},
	{ deep: true }
);

// 计算总金额
const calculateTotal = lists => {
	const checkedItems = lists
		.map(item => item.list || [])
		.flat()
	.filter(item => item.checked);

	totalSum.value = 0;
	showMaskedTotal.value = checkedItems.some(item => isCooperationPrice(item));
	if (showMaskedTotal.value) return;
	checkedItems.forEach(item => {
		const price = parseFloat(item.price) || 0;
		const quantity = parseFloat(item.quantity) || 0;
		totalSum.value = math.add(totalSum.value, math.multiply(price, quantity));
	});
};

// 获取购物车活动（优惠券）
const getShoppingCartActivityHandler = async () => {
	try {
		for (let item of props.lists) {
			const checkList = item.list
				.filter(item => item.checked)
				.map(item => ({
					productId: item.productId,
					productCode: item.productCode,
					productPrice: item.price,
					purchaseQuantity: item.quantity,
					agreementPriceId: item.agreementPriceId,
					imageUrl: item.imageUrl,
					checked: false,
					activityUseSgin: 1,
					areaName: item.areaName,
				}));

			if (checkList.length > 0) {
				const res = await getShoppingCartActivity({
					companyCode: item.companyCode,
					couponUseSgin: 1,
					orderProductList: checkList,
				});

				if (res.code == 200 && res.data) {
					if (res.data.couponActivityVo && res.data.couponActivityVo.length) {
						item.couponActivityVo = res.data.couponActivityVo;

						// 判断有几张券
						const couponList = res.data.couponActivityVo.filter(c => c.chooseSgin === 1);

						if (couponList.length > 0) {
							if (couponList.length === 1) {
								setStorageSync('userReceiveId', couponList[0].userReceiveId);
								setStorageSync('userReceiveId2', '');
							} else if (couponList.length === 2) {
								setStorageSync('userReceiveId', couponList[0].userReceiveId);
								setStorageSync('userReceiveId2', couponList[1].userReceiveId);
							}
						}
					} else {
						item.showActivity = '';
						item.chooseUserReceiveId = '';
						item.couponAmount = 0;
						item.chooseCouponType = 0;
						item.chooseUserReceiveId2 = '';
					}

					// 更新商品信息
					if (res.data.orderProductList && res.data.orderProductList.length) {
						item.list.forEach(item1 => {
							res.data.orderProductList.forEach(item2 => {
								if (item1.productId === item2.productId && item1.price === item2.productPrice) {
									for (let p in item2) {
										if (p !== 'checked') {
											item1[p] = item2[p];
										}
									}
								}
							});
						});
					}
				}
			}
		}
	} catch (error) {
		console.error('获取购物车活动失败:', error);
	}
};

// 提交订单
const handleSubmit = async () => {
	try {
		await trackEvent({
			module: '购物车',
			eventCode: 'cart_page_click',
			eventName: '购物车点击',
			attrName: 'button_name',
			attrCode: '提交需求单',
		});
	} catch (e) {
		console.error(e);
	}
	// 先获取购物车活动
	await getShoppingCartActivityHandler();

	const checkList = props.lists
		.map(item => item.list || [])
		.flat()
		.filter(item => item.checked);

	const couponList = props.lists
		.filter(item => item.list && item.list.some(list => list.checked))
		.map(item => item.couponActivityVo || [])
		.flat()
		.filter(item => item.chooseSgin === 1);

	if (!checkList.length) {
		showToast({
			message: '你还没有选择商品哦',
			icon: 'none',
			duration: 2000,
		});
		return;
	}

	// 保存选中商品和优惠券到 localStorage（orderList 使用原生 localStorage）
	localStorage.setItem('orderList', JSON.stringify(checkList));
	setStorageSync('couponList', couponList);

	// 跳转到订单确认页
	const navigateToOld = useNuxtApp().$navigateToOld;
	navigateToOld('/pages/buyCar/order/order');
};

// 全选点击事件
const handleClick = () => {
	emit('selectAll', isChecked.value);
};

// 供外部调用的方法
const changeAll = val => {
	isChecked.value = val;
};

defineExpose({
	changeAll,
});
</script>

<style scoped lang="scss">
.bot-bar {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	padding: 8px 12px 8px 10px;
	padding-bottom: calc(8px + env(safe-area-inset-bottom));
	background: #ffffff;
	box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
	position: fixed;
	bottom: 50px;
	left: 0;
	right: 0;
	z-index: 999;
	height: 52px;

	.select-all {
		display: flex;
		align-items: center;
		gap: 9px;
		font-family: 'Source Han Sans', '思源黑体', sans-serif;
		font-size: 14px;
		font-weight: normal;
		line-height: 21px;
		color: #333333;

		:deep(.van-checkbox__label) {
			font-size: 14px;
		}
	}

	.submit-section {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		gap: 11px;

		.total-amount {
			display: flex;
			align-items: center;
			line-height: 21px;
			font-size: 14px;
			color: #222;
			text-align: right;

			.label {
				position: relative;
				font-family: 'Source Han Sans', '思源黑体', sans-serif;
				font-size: 14px;
				font-weight: normal;
				line-height: 21px;
				color: #1f1f1f;
				margin-right: 4px;
			}

			:deep(.show-amount) {
				font-size: 14px;
				color: #ea2525;
			}

			.masked-total {
				font-size: 14px;
				color: #ea2525;
				font-weight: 500;
				line-height: 21px;
			}
		}

		.submit-btn {
			width: 100px;
			height: 36px;
			font-size: 14px;
			font-weight: 500;
			border-radius: 4px;
		}
	}
}
</style>
